import { z } from "zod";

enum Status {
  ACTIVE = "active",
  ONBOARDING = "onboarding",
}

type Store = z.infer<typeof StoreSchema>;

type GetStoreInput = z.infer<typeof GetStoreInputSchema>;

type GetStoreByIdInput = z.infer<typeof GetStoreByIdInputSchema>;

type GetStoreBySlugInput = z.infer<typeof GetStoreBySlugInputSchema>;

type GetStoreOutput = z.infer<typeof GetStoreOutputSchema>;

type Address = z.infer<typeof AddressSchema>;

type Social = z.infer<typeof SocialSchema>;

const StoreIdSchema = z.uuidv7();

const StoreSlugSchema = z.string().min(1);

const AddressSchema = z
  .object({
    zipCode: z.string().nullable().default(null),
    street: z.string().nullable().default(null),
    number: z.string().nullable().default(null),
    complement: z.string().nullable().default(null),
    district: z.string().nullable().default(null),
    city: z.string().nullable().default(null),
    state: z.string().nullable().default(null),
    showMapPreview: z.boolean().default(false),
    showAddress: z.boolean().default(false),
  })
  .optional()
  .nullable()
  .default(null);

const SocialSchema = z
  .object({
    instagram: z.string().nullable().default(null),
    facebook: z.string().nullable().default(null),
    tiktok: z.string().nullable().default(null),
    x: z.string().nullable().default(null),
  })
  .optional()
  .nullable()
  .default(null);

const PaymentMethodsSchema = z.object({
  online: z.object({
    enabled: z.boolean(),
  }),
  onDelivery: z.object({
    enabled: z.boolean(),
  }),
  paymentLink: z.object({
    enabled: z.boolean(),
  }),
});

const PreCheckoutSchema = z.object({
  pickup: z.object({
    enabled: z.boolean(),
  }),
  delivery: z.object({
    enabled: z.boolean(),
    availableRegions: z.string(),
    hasDeliveryCharge: z.boolean(),
    deliveryPrice: z.number(),
    hasNegotiableDelivery: z.boolean(),
  }),
  paymentMethods: PaymentMethodsSchema,
  stock: z.object({
    enabled: z.boolean(),
  }),
});

const OpeningHourForDaySchema = z
  .object({
    closed: z.boolean(),
    intervals: z
      .array(
        z.object({
          openAt: z.string(),
          closeAt: z.string(),
        })
      )
      .nullable(),
  })
  .optional()
  .nullable()
  .default(null);

const OpeningHoursSchema = z
  .object({
    days: z.object({
      friday: OpeningHourForDaySchema,
      holidays: OpeningHourForDaySchema,
      monday: OpeningHourForDaySchema,
      saturday: OpeningHourForDaySchema,
      sunday: OpeningHourForDaySchema,
      thursday: OpeningHourForDaySchema,
      tuesday: OpeningHourForDaySchema,
      wednesday: OpeningHourForDaySchema,
    }),
    openingMode: z
      .enum(["always_open", "always_closed", "scheduled"])
      .nullable(),
    timezone: z.string().nullable(),
  })
  .optional()
  .nullable()
  .default(null);

const VitrineSchema = z.object({
  applyThemeHeader: z.boolean().optional().nullable().default(true),
  backgroundImage: z
    .object({
      id: z.string(),
      url: z.string(),
    })
    .optional()
    .nullable()
    .default(null),
  brandLogo: z
    .object({
      id: z.string(),
      url: z.string(),
    })
    .optional()
    .nullable()
    .default(null),
  email: z.email().optional().nullable().default(null),
  metatags: z
    .object({
      description: z.string().nullable().default(null),
      title: z.string().nullable().default(null),
    })
    .optional()
    .nullable()
    .default(null),
  name: z.string().optional().nullable().default(null),
  slug: z.string().optional().nullable().default(null),
  theme: z.string().optional().nullable().default(null),
  visible: z.boolean().optional().nullable().default(true),
  tonUrl: z.string().optional().nullable().default(""),
  stoneUrl: z.string().optional().nullable().default(""),
  whatsAppNumber: z.string().optional().nullable().default(""),
  whatsAppSharingEnabled: z.boolean().optional().nullable().default(false),
  address: AddressSchema,
  social: SocialSchema,
  preCheckout: PreCheckoutSchema,
  createdAt: z.coerce.date().optional().nullable().default(null),
  updatedAt: z.coerce.date().optional().nullable().default(null),
  isOpenNow: z.boolean().optional().nullable().default(true),
  openingHours: OpeningHoursSchema,
});

const StoreSchema = z.object({
  id: StoreIdSchema,
  identifier: z.union([StoreIdSchema, StoreSlugSchema]).optional().default(""),
  name: z.string(),
  document: z.string(),
  vitrine: VitrineSchema,
});

const GetStoreByIdInputSchema = z.object({
  storeId: StoreIdSchema,
});

const GetStoreBySlugInputSchema = z.object({
  slug: StoreSlugSchema,
});

const GetStoreInputSchema = z.object({
  identifier: z.union([StoreIdSchema, StoreSlugSchema]),
});

const GetStoreOutputSchema = StoreSchema;

export {
  Status,
  StoreIdSchema,
  StoreSlugSchema,
  StoreSchema,
  GetStoreByIdInputSchema,
  GetStoreInputSchema,
  GetStoreBySlugInputSchema,
  GetStoreOutputSchema,
  AddressSchema,
  SocialSchema,
  type Store,
  type GetStoreInput,
  type GetStoreByIdInput,
  type GetStoreBySlugInput,
  type GetStoreOutput,
  type Address,
  type Social,
};
